import { EventEmitter, Injectable, inject } from '@angular/core';
import { AABB } from '../geometry';
import { FACE_DETECTION_SCRIPT } from '../face-detection';
import { StoreService } from '@shared/store';
import { selectDebugSettings } from '@shared/store/slices/debug';

export interface FaceDetectorResult {
  x: number;
  y: number;
  width: number;
  height: number;
  blur: number;
}

export interface WorkerData {
  width: number; // The image width
  height: number; // The image height
  data: Uint8ClampedArray; // The image data

  // Coordinates for the region of interest (ROI)
  roiLeft: number;
  roiTop: number;
  roiWidth: number;
  roiHeight: number;
}

@Injectable({
  providedIn: 'root',
})
export class FaceTrackerService {
  private readonly store = inject(StoreService);

  public facesDetected = new EventEmitter<number>();

  private worker: Worker;
  private faceDetectionActive: boolean;
  private busy = false;
  private debugCanvas: HTMLCanvasElement;
  private debugStats: HTMLDivElement;

  /**
   * Start tracking a region of interest in a video.
   */
  public start() {
    if (this.faceDetectionActive) {
      return;
    }

    // Debugging UI
    const debug = this.store.select(selectDebugSettings());
    if (debug.enabled && debug.enableFaceTrackerUi) {
      this.debugCanvas = document.createElement('canvas');
      this.debugCanvas.style.position = 'absolute';
      this.debugCanvas.style.left = '0';
      this.debugCanvas.style.top = '0';
      this.debugCanvas.style.right = '0';
      this.debugCanvas.style.bottom = '0';
      this.debugCanvas.style.width = '100%';
      this.debugCanvas.style.height = '100%';
      this.debugCanvas.style.transform = 'scaleX(-1)';
      this.debugCanvas.style.zIndex = '999999';
      document.body.appendChild(this.debugCanvas);
      this.debugStats = document.createElement('div');
      this.debugStats.style.position = 'absolute';
      this.debugStats.style.left = '0';
      this.debugStats.style.bottom = '80px';
      this.debugStats.style.zIndex = '999999';
      this.debugStats.style.color = '#00ff00';
      document.body.appendChild(this.debugStats);
    }

    this.faceDetectionActive = true;
    this.worker = new Worker(URL.createObjectURL(new Blob([`(${FACE_DETECTION_SCRIPT.toString()})()`])));
    this.worker.onmessage = (event: MessageEvent<FaceDetectorResult | undefined>) => {
      if (event.data) {
        const result = event.data;
        if (this.debugCanvas) {
          this.updateDebugInterface(result);
        }
        this.facesDetected.emit(result.blur > 1 ? 1 : 0); // blur threshold may need tuning
      } else {
        this.facesDetected.emit(0);
      }
      this.busy = false;
    };
  }

  public submitFrame(imageData: ImageData, roi: AABB) {
    if (this.busy) {
      console.warn('FaceTracker: frame dropped');
      return;
    }
    const workerData: WorkerData = {
      data: imageData.data,
      width: imageData.width,
      height: imageData.height,
      roiLeft: roi.x,
      roiTop: roi.y,
      roiWidth: roi.width,
      roiHeight: roi.height,
    };
    this.busy = true;
    this.worker.postMessage(workerData);
    if (this.debugCanvas && this.debugCanvas.width !== imageData.width) {
      this.debugCanvas.width = imageData.width;
      this.debugCanvas.height = imageData.height;
    }
  }

  public stop() {
    if (this.faceDetectionActive) {
      this.faceDetectionActive = false;
      this.worker.terminate();
      this.worker = null;
    }
    this.busy = false;

    if (this.debugCanvas) {
      this.debugCanvas.remove();
      this.debugCanvas = null;
      this.debugStats.remove();
      this.debugStats = null;
    }
  }

  //#region Debug

  private updateDebugInterface(result: FaceDetectorResult) {
    const green = '#00ff00';
    const red = '#ff0000';

    const canvas = this.debugCanvas;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = result.blur > 1 ? green : red;
    ctx.strokeRect(result.x, result.y, result.width, result.height);

    this.debugStats.innerHTML = `Face: ${result.x}, ${result.y}; ${result.width} x ${result.height} blur=${result.blur}<br>`;
    this.debugStats.innerHTML += `Video source: ${this.debugCanvas.width} x ${this.debugCanvas.height}<br>`;
  }

  //#endregion Debug
}
